// 已知的 pulse Survey API host。widget 對外 base origin 僅允許這些 host——
// 定位為防誤設／部分入侵（D3=A：宿主可信，非防惡意宿主 sandbox escape）。
export const ALLOWED_API_HOSTS: readonly string[] = [
  'api-survey.pulse.lab.tvbs-staging.app',
  'api-survey.pulse.lab.tvbs.app',
]

// 解析成功、https、且 host（含 port）為 allowlist 之一才 true。
// 兩個問題共用這支述詞：build 時「這個 baked 值可以出貨嗎」（build/assert-api-origin.ts）、
// runtime 時「這個 override 收不收」（decideApiBase）。兩者是不同決策，只是判準相同。
export function isAllowedApiHost(url: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(url)
  }
  catch {
    return false
  }
  return parsed.protocol === 'https:' && ALLOWED_API_HOSTS.includes(parsed.host)
}

/**
 * 一次請求要打哪個 base，以及為什麼。
 *
 * 三臂是政策本身的三條路徑，不是回傳值的三種格式：呼叫端只想知道 `base` 時讀 `base` 就好，
 * 但 `outcome` 讓「用了 baked」與「用了 override」在斷言裡分得開——兩者的 `base` 可能剛好同值，
 * 只比對字串的測試會在那時證明不了任何事。
 */
export type ApiBaseDecision
  = | { readonly outcome: 'baked', readonly base: string }
    | { readonly outcome: 'override', readonly base: string }
  // 被拒絕的 override 原樣帶出來，讓呼叫端說得出「忽略掉的是什麼」。型別是 unknown 而非 string：
  // 非字串的 override 也走這一臂。
    | { readonly outcome: 'rejected', readonly base: string, readonly override: unknown }

/**
 * base URL 政策的唯一所在地：baked 值、host override 與所在環境進來，決定這次請求用哪個 base。
 *
 * 收下 `baked` / `isDev` 而不自己讀 `import.meta.env`，是為了讓政策不必經過網路才驗得到——
 * 讀全域那一格留在 api.ts 的 resolveBaseUrl。
 *
 * override 收緊規則：DEV 接受任意字串（showcase→mock、localhost）；production 只認 allowlist。
 * override 來自宿主注入的 global，runtime 型別不可信（可能是物件／數字），故先擋非字串，
 * 確保 base 永遠是字串。falsy 的 override（未設定、空字串）等同沒設定，直接用 baked、不算被拒。
 */
export function decideApiBase(input: { baked: string, override: unknown, isDev: boolean }): ApiBaseDecision {
  const { baked, override, isDev } = input
  if (!override)
    return { outcome: 'baked', base: baked }
  if (typeof override === 'string' && (isDev || isAllowedApiHost(override)))
    return { outcome: 'override', base: override }
  return { outcome: 'rejected', base: baked, override }
}
